import React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { UserCog, Settings as SettingsIcon, ScrollText, ArrowRight, Shield, Database, ShieldCheck, CheckCircle2, Palette, FileCode2, Lock } from 'lucide-react';

const TABS = [
  { to: '/settings/users', label: 'Users', icon: UserCog, roles: ['super_admin', 'admin'] },
  { to: '/settings/roles', label: 'Roles & Permissions', icon: ShieldCheck, roles: ['super_admin'] },
  { to: '/settings/approvals', label: 'Approval Workflow', icon: CheckCircle2, roles: ['super_admin'] },
  { to: '/settings/dropdowns', label: 'Add Company', icon: SettingsIcon, roles: ['super_admin', 'admin'] },
  { to: '/settings/branding', label: 'Form Branding', icon: Palette, roles: ['super_admin', 'admin'] },
  { to: '/settings/security', label: 'Security', icon: Lock, roles: ['super_admin'] },
  { to: '/settings/data', label: 'Data Management', icon: Database, roles: ['super_admin'] },
  { to: '/settings/audit-logs', label: 'Audit Trail', icon: ScrollText, roles: ['super_admin'] },
  { to: '/settings/tech-handbook', label: 'Tech Handbook', icon: FileCode2, roles: ['super_admin'] },
];

const SettingsLanding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cards = [
    {
      to: '/settings/users',
      title: 'User Management',
      description: 'Create, edit, and manage user accounts. Assign roles and reset passwords.',
      icon: UserCog,
      roles: ['super_admin', 'admin'],
      accent: 'bg-blue-50 text-[hsl(217,91%,24%)]',
    },
    {
      to: '/settings/roles',
      title: 'Roles & Permissions',
      description: 'Create custom roles and configure granular access for every module and action.',
      icon: ShieldCheck,
      roles: ['super_admin'],
      accent: 'bg-violet-50 text-violet-700',
    },
    {
      to: '/settings/approvals',
      title: 'Approval Workflow',
      description: 'Designate who can approve equipment requests, inspections, mobilizations, and maintenance.',
      icon: CheckCircle2,
      roles: ['super_admin'],
      accent: 'bg-emerald-50 text-emerald-700',
    },
    {
      to: '/settings/dropdowns',
      title: 'Add Company',
      description: 'Manage centralized dropdown options (companies, areas, locations, categories, NC II).',
      icon: SettingsIcon,
      roles: ['super_admin', 'admin'],
      accent: 'bg-amber-50 text-amber-700',
    },
    {
      to: '/settings/branding',
      title: 'Form Branding',
      description: 'Customize mobilization form header, logo, footer disclaimer, and tracking URL.',
      icon: Palette,
      roles: ['super_admin', 'admin'],
      accent: 'bg-fuchsia-50 text-fuchsia-700',
    },
    {
      to: '/settings/security',
      title: 'Security',
      description: 'Session idle timeout, password policy, and brute-force protection.',
      icon: Lock,
      roles: ['super_admin'],
      accent: 'bg-amber-50 text-amber-700',
    },
    {
      to: '/settings/data',
      title: 'Data Management',
      description: 'Backup, restore, and purge system data. Password-protected destructive actions.',
      icon: Database,
      roles: ['super_admin'],
      accent: 'bg-emerald-50 text-emerald-700',
    },
    {
      to: '/settings/audit-logs',
      title: 'Audit Trail',
      description: 'Immutable log of all critical actions and changes across the system.',
      icon: ScrollText,
      roles: ['super_admin'],
      accent: 'bg-violet-50 text-violet-700',
    },
    {
      to: '/settings/tech-handbook',
      title: 'Tech Handbook',
      description: 'Confidential architecture, code-paths, env vars & migration runbook. Printable as PDF for cloud transfers.',
      icon: FileCode2,
      roles: ['super_admin'],
      accent: 'bg-slate-100 text-slate-800',
    },
  ];

  const visible = cards.filter((c) => hasRole(user, ...c.roles));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {visible.map(({ to, title, description, icon: Icon, accent }) => (
        <button key={to} onClick={() => navigate(to)} className="text-left group">
          <Card className="h-full transition-shadow hover:shadow-lg hover:border-[hsl(217,91%,24%)]">
            <CardContent className="p-6 space-y-3">
              <div className={`h-12 w-12 rounded-xl grid place-items-center ${accent}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">{title}</h3>
                <p className="text-sm text-slate-500 mt-1">{description}</p>
              </div>
              <div className="inline-flex items-center text-sm font-medium text-[hsl(217,91%,24%)] group-hover:gap-2 transition-all">
                Open <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </button>
      ))}
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isLanding = location.pathname === '/settings' || location.pathname === '/settings/';

  const visibleTabs = TABS.filter((t) => hasRole(user, ...t.roles));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Settings"
        description="System administration: users, dropdown options, and audit trail."
      />

      {/* Tab navigation */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
        <NavLink
          to="/settings"
          end
          className={({ isActive }) =>
            `inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              isActive ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`
          }
        >
          <Shield className="h-4 w-4" /> Overview
        </NavLink>
        {visibleTabs.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                isActive ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'
              }`
            }
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </div>

      {isLanding ? <SettingsLanding /> : <Outlet />}
    </div>
  );
};

export default Settings;
